import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { ReturnTimeslotView, TimeSlotValue } from '../../models/APIModels';
import ReturnTimeslotPicker from '../ReturnTimeslotPicker';
import Button from '../Button';

interface ReturnTimeSlotModalProps {
  show: boolean;
  close: () => void;
  /** Eligible returns, as reported by GET /order/return-timeslots/eligible. */
  eligible: ReturnTimeslotView[];
  /** Delivery date per order id, for the "on Monday 24 August" line. */
  deliveryDates?: Record<number, number | string | null | undefined>;
  onBooked?: (orderId: number, slot: TimeSlotValue) => void;
}

/**
 * Modal wrapper for the return-delivery timeslot picker, opened from the dashboard
 * CTA. Mirrors the collection TimeSlotModal shell (`modern-modal__*`) so the two
 * upsells behave identically — same overlay, same close affordance, same
 * click-outside-to-dismiss.
 *
 * The picker itself is not duplicated here. It re-reads
 * `GET /order/{id}/return-timeslot` on mount, which is what satisfies the brief's
 * "re-check eligibility when the customer clicks" requirement: the dashboard list
 * can be seconds to minutes stale, and the modal must not offer a slot that has
 * since lapsed.
 */
export function ReturnTimeSlotModal({
  show,
  close,
  eligible,
  deliveryDates,
  onBooked,
}: ReturnTimeSlotModalProps): JSX.Element | null {
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [orderIds, setOrderIds] = useState<number[]>([]);
  const wasShown = useRef(false);

  /*
    Snapshot the eligible order ids when the modal opens, and render from the
    snapshot rather than the live prop.

    Booking removes the order from the parent's eligible list — that is what makes
    the dashboard CTA go away. If this modal read that list directly, the successful
    booking would empty it and tear down the picker showing the confirmation the
    customer just earned. The snapshot keeps the open modal stable; the CTA behind it
    still updates.
  */
  useEffect(() => {
    if (show && !wasShown.current) {
      const ids = eligible.map((item) => item.orderId);
      setOrderIds(ids);
      setSelectedOrderId(ids.length > 0 ? ids[0] : null);
    }
    wasShown.current = show;
  }, [show, eligible]);

  if (!show) return null;

  return ReactDOM.createPortal(
    <div className="modern-modal__overlay" onClick={close}>
      <div
        className="modern-modal__container timeslot-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modern-modal__header">
          <h2>Book Your Delivery Time-Slot</h2>
          <Button
            variant="tertiary"
            size="sm"
            iconOnly
            className="modern-modal__close"
            aria-label="Close"
            onClick={close}
          >
            <XMarkIcon aria-hidden="true" />
          </Button>
        </div>

        <div className="timeslot-modal__body">
          {selectedOrderId === null ? (
            <p className="timeslot-modal__info-text">
              None of your current orders can be given a delivery time-slot at the
              moment. Please contact us if you need help with your delivery time.
            </p>
          ) : (
            <>
              {orderIds.length > 1 && (
                <div className="timeslot-modal__order-select">
                  <label htmlFor="return-timeslot-order">Select Order</label>
                  <select
                    id="return-timeslot-order"
                    value={selectedOrderId}
                    onChange={(e) => setSelectedOrderId(Number(e.target.value))}
                  >
                    {orderIds.map((orderId) => (
                      <option key={orderId} value={orderId}>
                        Order #{orderId}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {orderIds.length === 1 && (
                <p className="timeslot-modal__order-label">
                  Order <strong>#{selectedOrderId}</strong>
                </p>
              )}

              {/*
                Keyed on the order id so switching orders remounts the picker rather
                than leaving the previous order's fetched state on screen.
              */}
              <ReturnTimeslotPicker
                key={selectedOrderId}
                orderId={selectedOrderId}
                deliveryDate={deliveryDates?.[selectedOrderId]}
                initialView={eligible.find(
                  (item) => item.orderId === selectedOrderId,
                )}
                onBooked={(slot) => onBooked?.(selectedOrderId, slot)}
              />
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ReturnTimeSlotModal;
